@file:Suppress("MagicNumber")

package top.wcpe.allincore.ui.runtime.animation

import kotlin.jvm.JvmStatic
import kotlin.math.PI
import kotlin.math.pow
import kotlin.math.sin

/**
 * 动画 easing 曲线集合。
 * 所有曲线满足 apply(0)=0、apply(1)=1，输入会先被 clamp 到 [0,1]。
 */
enum class Easing {
    LINEAR {
        override fun apply(t: Double): Double = clamp(t)
    },
    EASE_IN {
        override fun apply(t: Double): Double {
            val x = clamp(t)
            return x * x
        }
    },
    EASE_OUT {
        override fun apply(t: Double): Double {
            val x = 1.0 - clamp(t)
            return 1.0 - x * x
        }
    },
    EASE_IN_OUT {
        override fun apply(t: Double): Double {
            val x = clamp(t)
            return if (x < 0.5) {
                2.0 * x * x
            } else {
                val y = -2.0 * x + 2.0
                1.0 - (y * y) / 2.0
            }
        }
    },
    EASE_IN_CUBIC {
        override fun apply(t: Double): Double {
            val x = clamp(t)
            return x * x * x
        }
    },
    EASE_OUT_CUBIC {
        override fun apply(t: Double): Double {
            val x = 1.0 - clamp(t)
            return 1.0 - x * x * x
        }
    },
    EASE_IN_OUT_CUBIC {
        override fun apply(t: Double): Double {
            val x = clamp(t)
            return if (x < 0.5) {
                4.0 * x * x * x
            } else {
                val y = -2.0 * x + 2.0
                1.0 - (y * y * y) / 2.0
            }
        }
    },
    EASE_IN_BACK {
        override fun apply(t: Double): Double {
            val x = clamp(t)
            val c1 = 1.70158
            val c3 = c1 + 1.0
            return c3 * x * x * x - c1 * x * x
        }
    },
    EASE_OUT_BACK {
        override fun apply(t: Double): Double {
            val x = clamp(t) - 1.0
            val c1 = 1.70158
            val c3 = c1 + 1.0
            return 1.0 + c3 * x * x * x + c1 * x * x
        }
    },
    EASE_OUT_BOUNCE {
        override fun apply(t: Double): Double {
            val x = clamp(t)
            val n1 = 7.5625
            val d1 = 2.75
            return when {
                x < 1.0 / d1 -> n1 * x * x
                x < 2.0 / d1 -> {
                    val y = x - 1.5 / d1
                    n1 * y * y + 0.75
                }
                x < 2.5 / d1 -> {
                    val y = x - 2.25 / d1
                    n1 * y * y + 0.9375
                }
                else -> {
                    val y = x - 2.625 / d1
                    n1 * y * y + 0.984375
                }
            }
        }
    },
    EASE_OUT_ELASTIC {
        override fun apply(t: Double): Double {
            val x = clamp(t)
            if (x == 0.0) return 0.0
            if (x == 1.0) return 1.0
            val c4 = (2.0 * PI) / 3.0
            return 2.0.pow(-10.0 * x) * sin((x * 10.0 - 0.75) * c4) + 1.0
        }
    }, ;

    abstract fun apply(t: Double): Double

    companion object {
        @JvmStatic
        fun fromName(name: String?): Easing {
            if (name == null) return LINEAR
            // KMP 迁移：lowercase() 即不变量区域（invariant locale）小写，与原 lowercase(Locale.ROOT) 等价
            val key = name.trim().lowercase().replace("_", "")
            return when (key) {
                "linear" -> LINEAR
                "easein", "easeinquad" -> EASE_IN
                "easeout", "easeoutquad" -> EASE_OUT
                "easeinout", "easeinoutquad" -> EASE_IN_OUT
                "easeincubic" -> EASE_IN_CUBIC
                "easeoutcubic" -> EASE_OUT_CUBIC
                "easeinoutcubic" -> EASE_IN_OUT_CUBIC
                "easeinback" -> EASE_IN_BACK
                "easeoutback" -> EASE_OUT_BACK
                "easeoutbounce" -> EASE_OUT_BOUNCE
                "easeoutelastic" -> EASE_OUT_ELASTIC
                else -> LINEAR
            }
        }

        /** 钳制 t 到 [0, 1]。包内由枚举实例与 AnimationScheduler 共用。 */
        @JvmStatic
        fun clamp(t: Double): Double = when {
            t <= 0.0 -> 0.0
            t >= 1.0 -> 1.0
            else -> t
        }
    }
}
